import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from "@tanstack/react-router";
import { store } from "./store";
import LobbyPage from "@pages/Lobby/ui/LobbyPage";
import DiscordCallbackPage from "@pages/DiscordCallback";
import FaceitCallbackPage from "@pages/FaceitCallback";
import { ShopLayout } from "@pages/VendingMachinePage/ShopLayout";
import { MainShop } from "@pages/VendingMachinePage/Main";
import { VipShop } from "@pages/VendingMachinePage/Vip";
import { CashShop } from "@pages/VendingMachinePage/Cash";
import { SkinsShop } from "@pages/VendingMachinePage/skins/Archive";
import { SelectGameLayout } from "@pages/VendingMachinePage/skins/SelectGame/SelectGameLayout";
import { SelectGameShop } from "@pages/VendingMachinePage/skins/SelectGame/SelectGameShop";
import { TopSkins } from "@pages/VendingMachinePage/skins/TopSkins";
import { CasesLayout } from "@pages/VendingMachinePage/Cases/BatlepassCases/CasesLayout";
import { SelectCasesPage } from "@pages/VendingMachinePage/Cases/BatlepassCases";
import { CasePage } from "@pages/VendingMachinePage/Cases/CasePage";
import Landing from "@pages/Landing/Landing";
import DemoPage from "@pages/Demo/DemoPage";
import { EmailVerificationPage } from "@pages/EmailVerification";
import LandingMobile from "@pages/Landing/LandingMobile";
import DemoMobilePage from "@pages/Demo/DemoMobilePage";

// Root Route
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  errorComponent: () => (
    <div className="error-page">
      <h1>Произошла ошибка</h1>
      <button onClick={() => (window.location.href = "/")}>
        Вернуться на главную
      </button>
    </div>
  ),
});

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Landing,
});

const landingMobileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/landing-mobile",
  component: LandingMobile,
});

const demoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/demo",
  component: DemoPage,
});

const demoMobileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/demo-mobile",
  component: DemoMobilePage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/oauth-callback",
  component: DiscordCallbackPage,
});

// Protected Routes
const lobbyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/lobby",
  component: LobbyPage,
});

// Shop Routes
const shopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shop",
  component: ShopLayout,
});

const shopIndexRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: "/",
  component: MainShop,
});

const shopSkinsRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: "/skins",
  component: SkinsShop,
});

const shopVipRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: "/vip",
  component: VipShop,
});

const shopCashRoute = createRoute({
  getParentRoute: () => shopRoute,
  path: "/cash",
  component: CashShop,
});

// Game Selection Routes
const selectGameLayout = createRoute({
  getParentRoute: () => shopRoute,
  path: "/select-game",
  component: SelectGameLayout,
});

const selectGameIndex = createRoute({
  getParentRoute: () => selectGameLayout,
  path: "/",
  component: SelectGameShop,
});

const topSkins = createRoute({
  getParentRoute: () => selectGameLayout,
  path: "/top-skins",
  component: TopSkins,
});

const archive = createRoute({
  getParentRoute: () => selectGameLayout,
  path: "/archive",
  component: SkinsShop,
});

// Cases Routes
const casesGameLayout = createRoute({
  getParentRoute: () => shopRoute,
  path: "/select-case",
  component: CasesLayout,
});

const selectCaseIndex = createRoute({
  getParentRoute: () => casesGameLayout,
  path: "/",
  component: SelectCasesPage,
});

const casePageRoute = createRoute({
  getParentRoute: () => casesGameLayout,
  path: "/case-page/$caseId",
  component: CasePage,
});

const faceitCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/faceit/callback",
  component: FaceitCallbackPage,
});

const emailVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-email",
  component: EmailVerificationPage,
});

// Route Tree
const routeTree = rootRoute.addChildren([
  landingRoute,
  landingMobileRoute,
  demoRoute,
  demoMobileRoute,
  callbackRoute,
  lobbyRoute,
  shopRoute.addChildren([
    shopIndexRoute,
    shopSkinsRoute,
    shopVipRoute,
    shopCashRoute,
    selectGameLayout.addChildren([selectGameIndex, topSkins, archive]),
    casesGameLayout.addChildren([selectCaseIndex, casePageRoute]),
  ]),
  faceitCallbackRoute,
  emailVerificationRoute,
]);

// Router Configuration
export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  context: store,
  defaultPreloadStaleTime: 1000 * 60 * 5,
  defaultErrorComponent: ({ error }) => (
    <div className="error-page">
      <h1>Произошла ошибка</h1>
      <p>{error.message}</p>
      <button onClick={() => (window.location.href = "/")}>
        Вернуться на главную
      </button>
    </div>
  ),
});

// Type Declarations
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
